import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDT } from "@/lib/format";
import { SectionTitle } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["stock_shortage", "qc_rejection", "packing_shortage", "documentation_pending", "vehicle_unavailable", "stock_mismatch", "quantity_mismatch", "customer_hold", "payment_hold", "production_pending", "delivery_shortage"];
const CAN_MANAGE = ["admin", "warehouse_manager", "supervisor", "qc", "accounts", "production"];

export default function Exceptions() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolveNote, setResolveNote] = useState("");
  const [form, setForm] = useState({ bill_no: "", type: "stock_shortage", owner: "", reason: "", expected_resolution: "" });
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = CAN_MANAGE.includes(user?.role);

  const load = useCallback(async () => {
    const res = await api.get("/exceptions");
    setItems(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post("/exceptions", { ...form, bill_no: form.bill_no || null, expected_resolution: form.expected_resolution || null });
      toast.success("Exception recorded");
      setOpen(false);
      setForm({ bill_no: "", type: "stock_shortage", owner: "", reason: "", expected_resolution: "" });
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
    }
  };

  const resolve = async () => {
    try {
      await api.post(`/exceptions/${resolveTarget.exception_id}/resolve`, { note: resolveNote || null });
      toast.success("Exception resolved");
      setResolveTarget(null);
      setResolveNote("");
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
    }
  };

  const open_items = items.filter((i) => i.status === "open");
  const closed_items = items.filter((i) => i.status !== "open");

  return (
    <div className="space-y-4" data-testid="exceptions-page">
      <SectionTitle
        right={
          canManage && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="new-exception-button"><Plus className="w-4 h-4 mr-1" /> New Exception</Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border" data-testid="exception-dialog">
                <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Record Exception</DialogTitle><DialogDescription className="sr-only">Record an operational exception and owner</DialogDescription></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Order (optional)</Label><Input data-testid="exception-bill-input" placeholder="SO-2026-01260" value={form.bill_no} onChange={(e) => setForm({ ...form, bill_no: e.target.value })} /></div>
                  <div>
                    <Label>Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger data-testid="exception-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Owner (accountable person)</Label><Input data-testid="exception-owner-input" value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} /></div>
                  <div><Label>Reason</Label><Textarea data-testid="exception-reason-input" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
                  <div><Label>Expected resolution</Label><Input data-testid="exception-resolution-input" value={form.expected_resolution} onChange={(e) => setForm({ ...form, expected_resolution: e.target.value })} /></div>
                  <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" onClick={create} data-testid="exception-create-submit">Record</Button>
                </div>
              </DialogContent>
            </Dialog>
          )
        }
      >
        Order Exceptions
      </SectionTitle>

      {[{ list: open_items, title: "Open", tone: "text-signal-red" }, { list: closed_items, title: "Resolved", tone: "text-signal-green" }].map(({ list, title, tone }) => (
        <div key={title} className="space-y-2">
          <h3 className={`text-lg font-semibold uppercase tracking-tight ${tone}`}>{title} ({list.length})</h3>
          {list.length === 0 && <p className="text-sm text-muted-foreground">None</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {list.map((e) => (
              <div key={e.exception_id} className={`bg-card border rounded-lg p-4 space-y-1.5 ${e.status === "open" ? "border-signal-red/40" : "border-border"}`} data-testid={`exception-${e.exception_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2">
                    {e.status === "open" && <AlertTriangle className="w-4 h-4 text-signal-red" />}
                    <span className="font-mono text-xs text-muted-foreground">{e.exception_id}</span>
                    <span className="capitalize font-semibold text-sm">{e.type.replace(/_/g, " ")}</span>
                  </span>
                  {e.bill_no && <button className="metric text-xs text-primary underline" onClick={() => navigate(`/orders/${e.bill_no}`)} data-testid={`exception-order-${e.exception_id}`}>{e.bill_no}</button>}
                </div>
                <p className="text-sm">{e.reason}</p>
                <p className="text-xs text-muted-foreground">Owner: <span className="font-medium text-foreground">{e.owner}</span> · {fmtDT(e.created_at)}</p>
                {e.expected_resolution && <p className="text-xs text-muted-foreground">Expected: {e.expected_resolution}</p>}
                {e.status === "open" && canManage && (
                  <Button size="sm" variant="outline" className="mt-1" onClick={() => { setResolveTarget(e); setResolveNote(""); }} data-testid={`resolve-${e.exception_id}`}>Resolve</Button>
                )}
                {e.status === "resolved" && <p className="text-xs text-signal-green">Resolved by {e.resolved_by} · {fmtDT(e.resolved_at)}{e.resolution_note ? ` — ${e.resolution_note}` : ""}</p>}
              </div>
            ))}
          </div>
        </div>
      ))}

      <Dialog open={!!resolveTarget} onOpenChange={() => setResolveTarget(null)}>
        <DialogContent className="bg-card border-border" data-testid="resolve-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Resolve {resolveTarget?.exception_id}</DialogTitle><DialogDescription className="sr-only">Resolve this exception with a note</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Resolution note</Label><Textarea data-testid="resolve-note-input" value={resolveNote} onChange={(e) => setResolveNote(e.target.value)} /></div>
            <Button className="w-full bg-signal-green hover:bg-signal-green/90 text-white" onClick={resolve} data-testid="resolve-submit-button">Mark Resolved</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
